/**
 * localStorage-backed progress state for the web app: translation count,
 * lifetime characters, daily streak, badge + mastery milestones, and
 * percentile standing. Mirrors the mobile app's stores/progressStore.ts
 * (MMKV-backed) shape closely enough that the two stay conceptually in
 * sync, but is a plain read/write module rather than a zustand store —
 * useProgress.ts is the reactive layer on top.
 */

import { getAuthEmail } from '../baba-auth'
import { BADGE_CATALOG, badgeForCount, crossedBadgeMilestone, nextBadge, type BadgeDefinition } from './badges'
import { crossedMasteryLevel, masteryForChars, type MasteryLevelDefinition, type MasteryProgress } from './mastery'
import { applyTranslation, computeStatus, emptyStreakState, type DailyStreakState, type StreakStatus } from './streak'

const KEYS = {
  TRANSLATION_COUNT: 'baba.progress.translationCount',
  LIFETIME_CHARACTERS: 'baba.progress.lifetimeCharacters',
  FIRST_OPEN: 'baba.progress.firstOpen',
  FIRST_OPEN_SERVER: 'baba.progress.firstOpenServer',
  HIGHEST_BADGE_CELEBRATED: 'baba.progress.highestBadgeCelebrated',
  HIGHEST_MASTERY_CELEBRATED: 'baba.progress.highestMasteryCelebrated',
  STREAK: 'baba.progress.streak',
  PERCENTILES: 'baba.progress.percentiles',
  HIGHEST_PERCENTILE_CELEBRATED: 'baba.progress.highestPercentileCelebrated',
} as const

export type PercentileTier = 'top1' | 'top5' | 'top10'

export interface PercentileThresholds {
  top1: number | null
  top5: number | null
  top10: number | null
}

export interface ProgressState {
  totalTranslations: number
  lifetimeCharacters: number
  firstOpen: number
  memberDays: number
  streak: DailyStreakState
  streakStatus: StreakStatus
  currentBadge: BadgeDefinition | null
  nextBadge: BadgeDefinition | null
  unlockedBadgeCount: number
  mastery: MasteryProgress
  percentiles: PercentileThresholds
  isTop1Percent: boolean
  isTop5Percent: boolean
  isTop10Percent: boolean
}

export type Celebration =
  | { kind: 'badge'; badge: BadgeDefinition }
  | { kind: 'mastery'; level: MasteryLevelDefinition }
  | { kind: 'percentile'; tier: PercentileTier }

function hasStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined'
  } catch {
    return false
  }
}

function readJson<T>(key: string, fallback: T): T {
  if (!hasStorage()) return fallback
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown): void {
  if (!hasStorage()) return
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage unavailable (private mode, quota) — fail silently, same as
    // the rest of the app's localStorage writes.
  }
}

function readNumber(key: string, fallback: number): number {
  if (!hasStorage()) return fallback
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return fallback
    const n = Number(raw)
    return Number.isFinite(n) ? n : fallback
  } catch {
    return fallback
  }
}

function writeNumber(key: string, value: number): void {
  if (!hasStorage()) return
  try {
    localStorage.setItem(key, String(value))
  } catch {
    // ignore
  }
}

function daysSince(timestamp: number): number {
  return Math.floor((Date.now() - timestamp) / (1000 * 60 * 60 * 24))
}

// Local-only fallback for a signed-out browser that has never synced: the
// first time this page loaded here. Never pushed to the server — see
// getServerFirstOpen below, which is what sync.ts echoes back.
function getOrInitFirstOpen(): number {
  const existing = readNumber(KEYS.FIRST_OPEN, 0)
  if (existing) return existing
  const now = Date.now()
  writeNumber(KEYS.FIRST_OPEN, now)
  return now
}

/**
 * The signed-in identity the cached server first-open is bound to. The
 * account's email is the same stable, synchronously-available identifier
 * lib/chatSync/identity.ts keys its per-account local state on: it survives
 * a JWT refresh (unlike the token itself) and is cleared on explicit sign-out
 * (baba-auth.ts's clearAuthToken), so a signed-out browser has no identity to
 * match against. Returns null for a signed-out browser, matching "no cached
 * value can apply here".
 */
function currentProgressIdentity(): string | null {
  return getAuthEmail()
}

interface CachedFirstOpen {
  identity: string
  value: number
}

/**
 * The account's real first-app-open, as last reported by GET /me. Present
 * only once mergeServerProgress has run at least once for a signed-in user,
 * AND only while the browser is still signed in as the SAME account that
 * value was cached for. Otherwise this browser-wide cache would leak one
 * account's timestamp into another account's session (or a signed-out visitor)
 * after a sign-out/sign-in swap. Authoritative when present: unlike
 * totals/characters this is never maxed against the local fallback, it simply
 * replaces it, matching the native app's "echo the server's own value, never
 * invent one" behaviour.
 */
export function getServerFirstOpen(): number | null {
  const identity = currentProgressIdentity()
  if (!identity) return null
  const cached = readJson<CachedFirstOpen | null>(KEYS.FIRST_OPEN_SERVER, null)
  if (!cached || cached.identity !== identity) return null
  return cached.value > 0 ? cached.value : null
}

function getStreak(): DailyStreakState {
  return readJson<DailyStreakState>(KEYS.STREAK, emptyStreakState())
}

function getPercentiles(): PercentileThresholds {
  return readJson<PercentileThresholds>(KEYS.PERCENTILES, { top1: null, top5: null, top10: null })
}

function buildState(): ProgressState {
  const totalTranslations = readNumber(KEYS.TRANSLATION_COUNT, 0)
  const lifetimeCharacters = readNumber(KEYS.LIFETIME_CHARACTERS, 0)
  const firstOpen = getServerFirstOpen() ?? getOrInitFirstOpen()
  const streak = getStreak()
  const percentiles = getPercentiles()

  const currentBadge = badgeForCount(totalTranslations)

  return {
    totalTranslations,
    lifetimeCharacters,
    firstOpen,
    memberDays: Math.max(1, daysSince(firstOpen)),
    streak,
    streakStatus: computeStatus(streak),
    currentBadge,
    nextBadge: nextBadge(totalTranslations),
    unlockedBadgeCount: countUnlockedBadges(totalTranslations),
    mastery: masteryForChars(lifetimeCharacters),
    percentiles,
    isTop1Percent: percentiles.top1 !== null && lifetimeCharacters >= percentiles.top1,
    isTop5Percent: percentiles.top5 !== null && lifetimeCharacters >= percentiles.top5,
    isTop10Percent: percentiles.top10 !== null && lifetimeCharacters >= percentiles.top10,
  }
}

function countUnlockedBadges(totalTranslations: number): number {
  return BADGE_CATALOG.filter((b) => totalTranslations >= b.milestone).length
}

/** Snapshot of current progress state, read fresh from localStorage. */
export function getProgressState(): ProgressState {
  return buildState()
}

/**
 * Call after a successful translation. Updates translation count, lifetime
 * characters, and the daily streak; persists all of it; and returns which
 * celebrations (badge unlock / mastery level-up / percentile tier) newly
 * became due, in priority order.
 */
export function recordTranslation({ chars }: { chars: number }): {
  state: ProgressState
  celebrations: Celebration[]
} {
  const previousTotal = readNumber(KEYS.TRANSLATION_COUNT, 0)
  const previousChars = readNumber(KEYS.LIFETIME_CHARACTERS, 0)

  const newTotal = previousTotal + 1
  const newChars = previousChars + Math.max(0, chars)

  writeNumber(KEYS.TRANSLATION_COUNT, newTotal)
  writeNumber(KEYS.LIFETIME_CHARACTERS, newChars)

  // Streak: one bump per calendar day, Shabbat-aware, freeze-covered.
  const streakResult = applyTranslation(getStreak(), new Date())
  writeJson(KEYS.STREAK, streakResult.state)

  const celebrations: Celebration[] = []

  const badge = crossedBadgeMilestone(newTotal, previousTotal)
  if (badge) {
    const highestCelebrated = readNumber(KEYS.HIGHEST_BADGE_CELEBRATED, 0)
    if (badge.milestone > highestCelebrated) {
      writeNumber(KEYS.HIGHEST_BADGE_CELEBRATED, badge.milestone)
      celebrations.push({ kind: 'badge', badge })
    }
  }

  const level = crossedMasteryLevel(newChars, previousChars)
  if (level) {
    const highestCelebrated = readNumber(KEYS.HIGHEST_MASTERY_CELEBRATED, 0)
    if (level.level > highestCelebrated) {
      writeNumber(KEYS.HIGHEST_MASTERY_CELEBRATED, level.level)
      celebrations.push({ kind: 'mastery', level })
    }
  }

  const percentileCelebration = checkPercentileCelebration(newChars, getPercentiles())
  if (percentileCelebration) celebrations.push({ kind: 'percentile', tier: percentileCelebration })

  return { state: buildState(), celebrations }
}

function tierRank(tier: PercentileTier): number {
  return tier === 'top1' ? 3 : tier === 'top5' ? 2 : 1
}

/**
 * Checks whether lifetimeChars newly crosses into a percentile tier that
 * hasn't been celebrated yet (highest tier wins, celebrated at most once).
 * Called both after a translation and whenever fresh thresholds arrive from
 * the server (sync.ts), since crossing a tier can also happen because the
 * threshold moved, not just because the user's count moved.
 */
function checkPercentileCelebration(lifetimeChars: number, thresholds: PercentileThresholds): PercentileTier | null {
  const highestCelebrated = readJson<PercentileTier | 'none'>(KEYS.HIGHEST_PERCENTILE_CELEBRATED, 'none')
  const reached: PercentileTier[] = []
  if (thresholds.top1 !== null && lifetimeChars >= thresholds.top1) reached.push('top1')
  if (thresholds.top5 !== null && lifetimeChars >= thresholds.top5) reached.push('top5')
  if (thresholds.top10 !== null && lifetimeChars >= thresholds.top10) reached.push('top10')
  if (reached.length === 0) return null

  const best = reached.sort((a, b) => tierRank(b) - tierRank(a))[0]
  const highestRank = highestCelebrated === 'none' ? 0 : tierRank(highestCelebrated)
  if (tierRank(best) <= highestRank) return null

  writeJson(KEYS.HIGHEST_PERCENTILE_CELEBRATED, best)
  return best
}

/**
 * Persist freshly-fetched percentile thresholds (from sync.ts) and report a
 * celebration if the user's existing character count newly qualifies.
 */
export function setPercentileThresholds(thresholds: PercentileThresholds): { celebration: Celebration | null } {
  writeJson(KEYS.PERCENTILES, thresholds)
  const lifetimeCharacters = readNumber(KEYS.LIFETIME_CHARACTERS, 0)
  const tier = checkPercentileCelebration(lifetimeCharacters, thresholds)
  return { celebration: tier ? { kind: 'percentile', tier } : null }
}

/** The subset of GET /me's response mergeServerProgress reads. */
export interface ServerProgressFields {
  total_translations?: number | string | null
  lifetime_characters_cached?: number | string | null
  settings?: {
    total_translations?: number | string | null
    lifetime_characters?: number | string | null
    streak_longest?: number | string | null
    first_app_open?: number | string | null
  } | null
}

function toFiniteNumber(value: unknown): number | null {
  const n = typeof value === 'string' ? Number(value) : value
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

/**
 * Merges server-reported progress (from GET /me, sync.ts) into the local
 * store, taking max() per counter exactly the way the native app does
 * (AppModel.swift:475-481, :520-529) — this is what lets a fresh browser
 * profile catch up to the account's real totals instead of showing only what
 * that one browser has ever translated, and it can never regress another
 * device's numbers. first_app_open is the one field NOT maxed: the server's
 * value is authoritative and is cached separately (getServerFirstOpen) so it
 * can be echoed back to /settings/sync unchanged, never re-derived locally.
 */
export function mergeServerProgress(server: ServerProgressFields | null | undefined): void {
  if (!server) return

  const serverTotal = Math.max(toFiniteNumber(server.total_translations) ?? 0, toFiniteNumber(server.settings?.total_translations) ?? 0)
  if (serverTotal > readNumber(KEYS.TRANSLATION_COUNT, 0)) writeNumber(KEYS.TRANSLATION_COUNT, serverTotal)

  const serverChars = Math.max(
    toFiniteNumber(server.lifetime_characters_cached) ?? 0,
    toFiniteNumber(server.settings?.lifetime_characters) ?? 0,
  )
  if (serverChars > readNumber(KEYS.LIFETIME_CHARACTERS, 0)) writeNumber(KEYS.LIFETIME_CHARACTERS, serverChars)

  const serverStreakLongest = toFiniteNumber(server.settings?.streak_longest)
  if (serverStreakLongest !== null && serverStreakLongest > 0) {
    const streak = getStreak()
    if (serverStreakLongest > streak.longestStreak) writeJson(KEYS.STREAK, { ...streak, longestStreak: serverStreakLongest })
  }

  const serverFirstOpen = toFiniteNumber(server.settings?.first_app_open)
  if (serverFirstOpen !== null && serverFirstOpen > 0) {
    // Bind the cache to whoever is signed in right now (this only runs after
    // a successful /me fetch, so there is one). No identity, no write: never
    // cache a value nothing can later be checked against.
    const identity = currentProgressIdentity()
    if (identity) writeJson(KEYS.FIRST_OPEN_SERVER, { identity, value: serverFirstOpen } satisfies CachedFirstOpen)
  }
}
